import { dehydrate, HydrationBoundary } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { Color } from '@diritto/common/style/color';
import { makeQueryClient } from '@/queries/options';
import { AppInstallBottomModal } from '@/ui-components/modals/AppInstallBottomModal';
import 'app/globals.css';
import Providers from 'app/provider';
import { AuthProvider } from 'contexts/AuthProvider';
import { InAppProvider } from 'contexts/InAppProvider';
import { TrackingProvider } from 'contexts/TrackingProvider';
import { GlobalStyle } from 'styles/global';
import { AlertModal } from 'ui-components/modals/AlertModal';
import { AppPushOnBottomModal } from 'ui-components/modals/AppPushOnBottomModal';
import { ConfirmModal } from 'ui-components/modals/ConfirmModal';
import { Modal } from 'ui-components/modals/Modal';
import { Toast } from 'ui-components/modals/Toast';
import { configure } from 'utils/configure';

export function BaseLayout({ children }: { children: React.ReactNode }) {
  const queryClient = makeQueryClient();

  return (
    <html lang="ko" suppressHydrationWarning>
      <head>
        <meta name="googlebot" content="noarchive" />
        <meta name="google-site-verification" content="OXcGjSkBu4uEFl1dRUXfG66lxzBsqOmpzD0ndvO-NS0" />
        <link rel="search" type="application/opensearchdescription+xml" title="디리토" href="/opensearch.xml" />
        <meta name="apple-itunes-app" content="app-id=1616806831" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="theme-color" content={Color.White} />
        <meta name="msapplication-navbutton-color" content={Color.White} />
        <meta name="apple-mobile-web-app-status-bar-style" content={Color.White} />
        <link
          rel="preload"
          href={`${configure.staticPrefix}/fonts/Pretendard/woff2-subset/Pretendard-Regular.subset.woff2`}
          as="font"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href={`${configure.staticPrefix}/fonts/Pretendard/woff2-subset/Pretendard-Medium.subset.woff2`}
          as="font"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href={`${configure.staticPrefix}/fonts/Pretendard/woff2-subset/Pretendard-Bold.subset.woff2`}
          as="font"
          crossOrigin="anonymous"
        />
        <link rel="dns-prefetch" href={configure.privateCdnPublicUrl} />
        <link rel="dns-prefetch" href={configure.cdnPublicUrl} />
        <meta name="naver-site-verification" content="d1c44c9a7bf2552ba44f7f5d7115fc0dab671d7f" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'http://schema.org',
              '@type': 'WebSite',
              url: 'https://www.diritto.co.kr',
              sameAs: [
                'https://x.com/diritto_kr/',
                'https://apps.apple.com/kr/app/%EB%94%94%EB%A6%AC%ED%86%A0-%EC%9E%90%EC%9C%A0%EC%97%B0%EC%9E%AC-%EC%9B%B9%EC%86%8C%EC%84%A4%EC%9D%98-%EC%8B%9C%EC%9E%91/id1616806831',
                'https://play.google.com/store/apps/details?id=kr.co.diritto',
              ],
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.diritto.co.kr/search/?q={q}&amp;referer=sitelinks_searchbox',
                'query-input': 'required name=q',
              },
            }),
          }}></script>
      </head>
      <GlobalStyle />
      <Providers>
        <HydrationBoundary state={dehydrate(queryClient)}>
          <InAppProvider>
            <AuthProvider>
              <TrackingProvider>
                <body>
                  {children}
                  <div id="__modal" />
                  <ReactQueryDevtools initialIsOpen={false} />
                </body>
                <Modal>
                  <AlertModal />
                  <ConfirmModal />
                  <Toast />
                  <AppPushOnBottomModal />
                  <AppInstallBottomModal />
                </Modal>
              </TrackingProvider>
            </AuthProvider>
          </InAppProvider>
        </HydrationBoundary>
      </Providers>
    </html>
  );
}
